
/*
 * Shared delivery-date maths for the plan builder and cart review.
 *
 * The cutoff is a STORE wall-clock rule ("Wednesday 22:00"), so it must be
 * evaluated against the store's clock, not the shopper's. Liquid renders
 * `{{ 'now' | date: '%Y-%m-%dT%H:%M:%S%z' }}` into each section's config; we
 * take only the UTC OFFSET from it, never the timestamp. Liquid's `'now'`
 * reflects when the page was last rendered and may be stale behind caching,
 * but the offset is stable for months, so pairing a cached offset with the
 * live browser instant stays correct.
 *
 * Every Date returned here is a "store-local" Date: its UTC fields hold the
 * store's wall-clock fields. Read them with getUTC*() and format them with
 * `timeZone: "UTC"` — using local getters re-introduces the shopper's
 * timezone and shifts the calendar day.
 */
if (!window.DeliverySchedule) {
  // The cutoff falls at 22:00 store time on the cutoff day: order before it
  // and you get the coming Sunday; at or after it, that Sunday is skipped and
  // delivery starts the following one. The TIME is fixed here — only the DAY
  // is merchant-configurable, so there is no hour/minute setting to read from
  // the section config. The reports app applies the same instant to recurring
  // orders (delivery-week.ts CUTOFF_HOUR / CUTOFF_MINUTE).
  const CUTOFF_HOUR = 22;
  const CUTOFF_MINUTE = 0;
  // Last-resort fallback only, for when the section config is missing or
  // unparseable. The real value is the `delivery_cutoff_day` theme setting,
  // which both sections render into their config blob.
  const DEFAULT_CUTOFF_DAY = 3; // Wednesday
  // How many weeks of first-delivery dates the calendar offers when the
  // section config carries no `weeksAhead` (theme setting `delivery_weeks_ahead`).
  const DEFAULT_WEEKS_AHEAD = 8;

  window.DeliverySchedule = {
    CUTOFF_HOUR,
    CUTOFF_MINUTE,
    DEFAULT_CUTOFF_DAY,
    DEFAULT_WEEKS_AHEAD,

    // "2026-08-21T12:20:00+0100" -> 60. Anything unparseable -> 0 (UTC), so a
    // missing or malformed config degrades to a defined date rather than NaN.
    parseOffsetMinutes(storeNow) {
      if (typeof storeNow !== "string") return 0;
      const m = storeNow.match(/([+-])(\d{2}):?(\d{2})$/);
      if (!m) return 0;
      const mins = parseInt(m[2], 10) * 60 + parseInt(m[3], 10);
      if (!Number.isFinite(mins)) return 0;
      return m[1] === "-" ? -mins : mins;
    },

    // Current instant from the browser (live, never stale), shifted into the
    // store's wall-clock frame.
    now(offsetMinutes) {
      return new Date(Date.now() + (offsetMinutes || 0) * 60000);
    },

    // The next cutoff instant that has not yet passed, as a store-local Date.
    // Sunday is a valid cutoff day (0), so the day is checked with
    // Number.isInteger — `cutoff.day || DEFAULT` would silently rewrite it.
    //
    // Split out of nextDeliverySunday so the hero countdown ticks down to the
    // very same instant the delivery dates are derived from. Two separate
    // implementations would drift apart the moment the cutoff day setting
    // changed, and a timer that disagrees with the delivery dates shown further
    // down the page is worse than no timer.
    nextCutoff(cutoff, offsetMinutes) {
      const day = cutoff && Number.isInteger(cutoff.day) ? cutoff.day : DEFAULT_CUTOFF_DAY;
      const now = this.now(offsetMinutes);

      const next = new Date(now);
      const daysToCutoff = (day - next.getUTCDay() + 7) % 7;
      next.setUTCDate(next.getUTCDate() + daysToCutoff);
      next.setUTCHours(CUTOFF_HOUR, CUTOFF_MINUTE, 0, 0);
      // At the cutoff instant itself the cutoff has passed (>=), matching the app.
      if (next <= now) next.setUTCDate(next.getUTCDate() + 7);
      return next;
    },

    // Milliseconds from the live browser instant until that cutoff. Both sides
    // of the subtraction carry the same store offset, so it cancels out and the
    // result is real elapsed time — safe to count down with.
    msUntilCutoff(cutoff, offsetMinutes) {
      return this.nextCutoff(cutoff, offsetMinutes).getTime() - this.now(offsetMinutes).getTime();
    },

    // First Sunday strictly after the next cutoff that has not yet passed.
    nextDeliverySunday(cutoff, offsetMinutes) {
      const nextCutoff = this.nextCutoff(cutoff, offsetMinutes);

      const sunday = new Date(nextCutoff);
      const daysToSunday = (7 - nextCutoff.getUTCDay()) % 7 || 7;
      sunday.setUTCDate(nextCutoff.getUTCDate() + daysToSunday);
      sunday.setUTCHours(0, 0, 0, 0);
      return sunday;
    },

    // Box 0 = the Sunday, box 1 = the Wednesday after it, box n = n weeks on.
    boxDate(sunday, index) {
      const d = new Date(sunday);
      if (index === 1) d.setUTCDate(sunday.getUTCDate() + 3);
      else if (index > 1) d.setUTCDate(sunday.getUTCDate() + 7 * index);
      return d;
    },

    // Store-local Date `n` days on. Pure — never mutates its input.
    addDays(d, n) {
      const out = new Date(d);
      out.setUTCDate(out.getUTCDate() + n);
      return out;
    },

    // Second box of a two-box plan: the Wednesday after the first box's Sunday.
    secondBoxDate(first) {
      return this.addDays(first, 3);
    },

    // Every date the customer may pick for the FIRST box, earliest first:
    // each deliverable Sunday for `weeksAhead` weeks from the next one the
    // cutoff allows, and — for single-box plans (`allowWednesday`) — the
    // Wednesday after each of those Sundays. A Wednesday therefore shares the
    // Sunday's cutoff: it is offered only once the Sunday before it is.
    firstDeliveryOptions(cutoff, offsetMinutes, opts) {
      const weeks = opts && Number.isInteger(opts.weeksAhead) && opts.weeksAhead > 0
        ? opts.weeksAhead
        : DEFAULT_WEEKS_AHEAD;
      const allowWednesday = Boolean(opts && opts.allowWednesday);
      const first = this.nextDeliverySunday(cutoff, offsetMinutes);
      const out = [];
      for (let week = 0; week < weeks; week++) {
        const sunday = this.addDays(first, 7 * week);
        out.push(sunday);
        if (allowWednesday) out.push(this.addDays(sunday, 3));
      }
      return out;
    },

    // "2026-09-13" — the cart-attribute form of a store-local Date. ISO so the
    // order tag sorts and parses without ambiguity.
    toISODate(d) {
      const y = d.getUTCFullYear();
      const m = String(d.getUTCMonth() + 1).padStart(2, "0");
      const day = String(d.getUTCDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },

    // "2026-09-13" -> store-local Date at midnight, or null. Cart attributes
    // are visitor-writable, so anything that is not exactly a real calendar
    // date (bad shape, "2026-02-31", junk) is rejected rather than coerced.
    fromISODate(str) {
      if (typeof str !== "string") return null;
      const m = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return null;
      const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
      if (Number.isNaN(d.getTime()) || this.toISODate(d) !== str) return null;
      return d;
    },

    // True when `date` is one of `options` (same calendar day).
    isOneOf(date, options) {
      if (!date) return false;
      const iso = this.toISODate(date);
      return options.some((d) => this.toISODate(d) === iso);
    },

    // The cart/order attributes for a plan's boxes, keyed by production BATCH:
    // batch 1 is the Sunday run, batch 2 the Wednesday run. Each key carries a
    // date only when the plan has a delivery in that batch:
    //   two boxes            batch_1_delivery_date: 2026-09-13  batch_2_delivery_date: 2026-09-16
    //   one box, Sunday      batch_1_delivery_date: 2026-09-13  batch_2_delivery_date: ""
    //   one box, Wednesday   batch_1_delivery_date: ""          batch_2_delivery_date: 2026-09-16
    // The unused key is blanked rather than omitted: /cart/update.js merges
    // attributes, and a blank is how a value from an earlier configuration is
    // removed. Shopify drops empty attributes, so the order never shows it.
    cartAttributes(dates) {
      const attrs = { batch_1_delivery_date: "", batch_2_delivery_date: "" };
      (dates || []).forEach((d) => {
        if (!d) return;
        if (d.getUTCDay() === 0) attrs.batch_1_delivery_date = this.toISODate(d);
        else if (d.getUTCDay() === 3) attrs.batch_2_delivery_date = this.toISODate(d);
      });
      return attrs;
    },

    // What replaces a first-box date that is no longer offered (its cutoff
    // passed while the page sat open, or the cart is old): the earliest
    // option on the same weekday, so a Wednesday box stays a Wednesday; else
    // the earliest option. Null when nothing is offered.
    replacementDate(date, options) {
      if (!options || !options.length) return null;
      const day = date ? date.getUTCDay() : null;
      return options.find((d) => d.getUTCDay() === day) || options[0];
    },

    // True when the cart carries exactly these batch dates (from
    // cartAttributes; a blank means the key must be absent). A cart write
    // that overlapped another can be dropped by Shopify, so a write's
    // returned cart is checked with this rather than trusted.
    cartHasDates(cartAttrs, expected) {
      if (!cartAttrs) return false;
      return Object.keys(expected).every((key) => (cartAttrs[key] || "") === expected[key]);
    },

    // Whether a cart's delivery dates can still be checked out:
    //   "missing" — fewer dated batches than the plan has boxes
    //   "passed"  — the first box is before the earliest date the cutoff allows
    //   "ok"
    // earliestSunday is the first deliverable Sunday (nextDeliverySunday). A
    // Wednesday first box shares that Sunday's cutoff, so it must fall on or
    // after the Wednesday that follows it.
    cartDateStatus(cartAttrs, earliestSunday, boxes) {
      const attrs = cartAttrs || {};
      const sunday = this.fromISODate(attrs.batch_1_delivery_date);
      const wednesday = this.fromISODate(attrs.batch_2_delivery_date);
      const dated = [sunday, wednesday].filter(Boolean).length;
      if (dated === 0 || dated < boxes) return "missing";
      const first = sunday || wednesday;
      const earliest = sunday ? earliestSunday : this.addDays(earliestSunday, 3);
      return first.getTime() < earliest.getTime() ? "passed" : "ok";
    },

    ordinal(n) {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    },

    // "Sunday 24 August"
    formatLong(d) {
      return d.toLocaleDateString("en-GB", {
        weekday: "long", day: "numeric", month: "long", timeZone: "UTC",
      });
    },

    // "Sunday, 13 September" — the delivery-date cards and calendar CTA.
    // en-GB drops the comma when weekday and date share one call, so the
    // weekday is formatted on its own and joined by hand.
    formatLongComma(d) {
      const weekday = d.toLocaleDateString("en-GB", { weekday: "long", timeZone: "UTC" });
      const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "long", timeZone: "UTC" });
      return `${weekday}, ${date}`;
    },

    // "September 2026" — the calendar's month heading.
    formatMonthYear(d) {
      return d.toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
    },

    // "Sun 24 Aug"
    formatShort(d) {
      return d.toLocaleDateString("en-GB", {
        weekday: "short", day: "numeric", month: "short", timeZone: "UTC",
      });
    },

    // "Sunday 24th"
    formatDayOrdinal(d) {
      const weekday = d.toLocaleDateString("en-GB", { weekday: "long", timeZone: "UTC" });
      return `${weekday} ${this.ordinal(d.getUTCDate())}`;
    },
  };
}
